import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService, ClaimAccountCommand } from '@anihis/core/data-access';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { translate } from '@jsverse/transloco';
import { catchError, finalize, map, of, tap } from 'rxjs';
import { LoadingStatus } from '../../shared/enums/loading';
import { extractServerErrorMessage } from '../../shared/functions/extract-server-error';

type ClaimAccountState = {
  status: LoadingStatus;
  linkStatus: LoadingStatus;
  error: string | null;
  linkValidated: boolean;
};

const initialState: ClaimAccountState = {
  status: LoadingStatus.Loaded,
  linkStatus: LoadingStatus.Loading,
  error: null,
  linkValidated: false,
};

export const ClaimAccountStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      snackBar = inject(MatSnackBar),
    ) => ({
      validateClaimLink(userId: string, token: string) {
        if (!userId || !token) {
          patchState(store, {
            linkStatus: LoadingStatus.Loaded,
            linkValidated: false,
            error: translate('Invalid claim link. Please request a new one.'),
          });
          return of(false);
        }

        patchState(store, {
          linkStatus: LoadingStatus.Loading,
          error: null,
          linkValidated: false,
        });

        return authService.authValidateClaimLinkPost({ userId, token }).pipe(
          tap(() => {
            patchState(store, {
              linkStatus: LoadingStatus.Loaded,
              linkValidated: true,
            });
          }),
          map(() => true),
          catchError((err) => {
            const errorMsg = extractServerErrorMessage(
              err,
              'Failed to claim account. The link may have expired.',
            );
            patchState(store, {
              linkStatus: LoadingStatus.Loaded,
              linkValidated: false,
              error: errorMsg,
            });
            snackBar.open(errorMsg, translate('Close'), {
              duration: 7000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            });
            return of(false);
          }),
        );
      },

      claimAccount(
        userId: string,
        token: string,
        newPassword: string,
        role: string,
      ) {
        if (!userId || !token) {
          snackBar.open(
            translate('Invalid claim link. Please request a new one.'),
            translate('Close'),
            {
              duration: 7000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            },
          );
          return of(false);
        }

        patchState(store, { status: LoadingStatus.Loading, error: null });

        const command: ClaimAccountCommand = { userId, token, newPassword };

        return authService.authClaimAccountPost(command).pipe(
          tap(() => {
            patchState(store, { status: LoadingStatus.Loaded });
            snackBar.open(
              translate(
                'Your account has been claimed successfully! You can now log in.',
              ),
              translate('Close'),
              {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['success-snackbar'],
              },
            );
          }),
          map(() => true),
          finalize(() => patchState(store, { status: LoadingStatus.Loaded })),
          catchError((err) => {
            const errors = err?.error?.errors;
            const errorMsg = errors
              ? Object.values(errors as Record<string, string[]>)
                  .flat()
                  .join(' ')
              : extractServerErrorMessage(
                  err,
                  'Failed to claim account. The link may have expired.',
                );

            patchState(store, {
              status: LoadingStatus.Loaded,
              error: errorMsg,
            });

            snackBar.open(errorMsg, translate('Close'), {
              duration: 7000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            });

            return of(false);
          }),
        );
      },
    }),
  ),
);
